import { EyeIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { type ICustomerEmail, type IEmailElement, EmailEditorModal } from 'components/Modals/EmailEditorModal'
import { SaveChanges } from 'components/SaveChanges'
import { BaseFile } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getAdminConfig,
  getEmailHardcodedDetail,
  getEmailPreview,
  setAdminConfig,
  updateEmailHardcoded,
} from 'services'
import { Modal, ToggleButton, Tooltip } from 'stories/components'
import { getPartiesMap } from 'utils'

export const EmailsHardcoded = () => {
  const allPlaceholders = Object.keys(getPartiesMap({}))

  const [isLoading, setLoading] = useState(false)
  const [isChanged, setChanged] = useState(false)
  const [titles, setTitles] = useState<Record<string, string>>({})
  const [data, setData] = useState<Record<string, boolean>>({})
  const [previewModal, setPreviewModal] = useState<{
    show: boolean
    emailKey: string
    subject: string
    html: string
    lastUpdatedAt: number
  }>({
    show: false,
    emailKey: '',
    subject: '',
    html: '',
    lastUpdatedAt: 0,
  })
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [editorModal, setEditorModal] = useState<{
    show: boolean
    emailKey: string
    subject: string
    elements: IEmailElement[]
    customerEmail: ICustomerEmail | null
    originalPlaceholders: string[]
    files: BaseFile[]
    supportsRecipients: boolean
    emailTo: string[]
    emailCc: string[]
    emailBcc: string[]
    emailExclude: string[]
  }>({
    show: false,
    emailKey: '',
    subject: '',
    elements: [],
    customerEmail: null,
    originalPlaceholders: [],
    files: [],
    supportsRecipients: false,
    emailTo: [],
    emailCc: [],
    emailBcc: [],
    emailExclude: [],
  })

  useEffect(() => {
    setLoading(true)
    getAdminConfig('emailHardcoded')
      .then((res) => {
        setData(res.data)
        setTitles(res.emailList)
      })
      .finally(() => setLoading(false))
  }, [])

  const onChange = (key: string, value: boolean) => {
    const newData = cloneDeep(data)
    newData[key] = value
    setData(newData)
    setChanged(true)
  }

  const onSave = () => {
    setLoading(true)
    setAdminConfig('emailHardcoded', data)
      .then(() => toast('Configuration is stored successfully.', { type: 'info' }))
      .finally(() => {
        setLoading(false)
        setChanged(false)
      })
  }

  const handlePreview = async (emailKey: string) => {
    setLoadingPreview(true)
    try {
      const preview = await getEmailPreview(emailKey)
      setPreviewModal({
        show: true,
        emailKey,
        subject: preview.subject,
        html: preview.html,
        lastUpdatedAt: Date.now(),
      })
    } catch (error) {
      toast('Failed to load email preview', { type: 'error' })
    } finally {
      setLoadingPreview(false)
    }
  }

  const closePreviewModal = () => {
    setPreviewModal({
      show: false,
      emailKey: '',
      subject: '',
      html: '',
      lastUpdatedAt: Date.now(),
    })
  }

  const handleEdit = async (emailKey: string) => {
    setLoadingPreview(true)
    try {
      const detail = await getEmailHardcodedDetail(emailKey)
      if (!detail) {
        toast('Email detail not found', { type: 'error' })
        return
      }
      setEditorModal({
        show: true,
        emailKey,
        subject: detail.subject,
        elements: detail.elements,
        customerEmail: detail.customerEmail,
        originalPlaceholders: detail.originalPlaceholders,
        files: detail.files || [],
        supportsRecipients: !!detail.supportsRecipients,
        emailTo: detail.emailTo || [],
        emailCc: detail.emailCc || [],
        emailBcc: detail.emailBcc || [],
        emailExclude: detail.emailExclude || [],
      })
    } catch {
      toast('Failed to load email detail', { type: 'error' })
    } finally {
      setLoadingPreview(false)
    }
  }

  const handleEditorSubmit = async (updated: {
    subject: string
    elements?: IEmailElement[]
    customerEmail?: ICustomerEmail | null
    files: BaseFile[]
    emailTo?: string[]
    emailCc?: string[]
    emailBcc?: string[]
    emailExclude?: string[]
  }) => {
    await updateEmailHardcoded(editorModal.emailKey, updated)
    toast('Email updated successfully.', { type: 'info' })
    setEditorModal((prev) => ({ ...prev, show: false }))
  }

  return (
    <div>
      <LayoutLoading show={isLoading || loadingPreview} />
      <SaveChanges show={isChanged} onSave={onSave} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {Object.keys(titles).map((key, index) => (
          <div key={key} className="mb-2 flex items-center gap-2">
            <div className="flex-1 flex gap-2 items-center">
              <ToggleButton
                id={key}
                title={`${index + 1}. ${titles[key]}`}
                value={data[key] === false ? false : true}
                onChange={(value) => onChange(key, value)}
                textLeft
              />
              <span
                className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                onClick={() => handlePreview(key)}
              >
                <EyeIcon className="w-5 h-5" />
              </span>
              <Tooltip
                message={
                  key === `loan.drawRequest`
                    ? 'This email is not editable here. Please edit in Conditions & Templates -> Loan Application Template'
                    : ''
                }
              >
                <span
                  className={`text-shade-blue ${
                    key === 'loan.drawRequest' ? 'cursor-not-allowed' : 'cursor-pointer hover-shadow1'
                  } p-1 rounded`}
                  onClick={() => handleEdit(key)}
                >
                  <PencilSquareIcon className="w-5 h-5" />
                </span>
              </Tooltip>
            </div>
          </div>
        ))}
      </div>

      <Modal
        title="Email Preview"
        isOpen={previewModal.show}
        lastUpdatedAt={previewModal.lastUpdatedAt}
        onClose={closePreviewModal}
        titleCancel=""
        titleOkay=""
      >
        <div className="w-[1200px]">
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-1">Subject:</h3>
            <p className="text-gray-900">{previewModal.subject}</p>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <iframe
              title="Email Preview"
              srcDoc={previewModal.html}
              className="w-full"
              style={{ height: '600px', border: 'none' }}
            />
          </div>
        </div>
      </Modal>

      {editorModal.show && (
        <EmailEditorModal
          isOpen={editorModal.show}
          onClose={() => setEditorModal((prev) => ({ ...prev, show: false }))}
          onSubmit={handleEditorSubmit}
          emailKey={editorModal.emailKey}
          subject={editorModal.subject}
          elements={editorModal.elements}
          customerEmail={editorModal.customerEmail}
          originalPlaceholders={editorModal.originalPlaceholders}
          placeholders={allPlaceholders}
          files={editorModal.files}
          supportsRecipients={editorModal.supportsRecipients}
          supportsCcBcc
          emailTo={editorModal.emailTo}
          emailCc={editorModal.emailCc}
          emailBcc={editorModal.emailBcc}
          emailExclude={editorModal.emailExclude}
        />
      )}
    </div>
  )
}
